import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';

import { MV_AUTH_TOKEN_STORAGE_KEY, VbLxpAuthService } from './vb-lxp-auth.service';

@Component({
  selector: 'vb-lxp-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [VbLxpAuthService],
  templateUrl: './vb-lxp-login.component.html',
  styleUrl: './vb-lxp-login.component.css',
})
export class VbLxpLoginComponent implements OnInit {
	private readonly authService = inject(VbLxpAuthService);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);

	protected readonly isSubmitting = signal(false);
	protected readonly showPassword = signal(false);
	protected readonly error = signal<string | null>(null);
	protected readonly title = signal('Sign In');
	protected readonly subtitle = signal('Use your credentials to continue');

	protected username = '';
	protected password = '';

	private loginPath = 'api/admin/login';
	private storageKey = MV_AUTH_TOKEN_STORAGE_KEY;
	private redirectTo = '/';

	ngOnInit(): void {
		this.title.set((this.route.snapshot.data['title'] as string) || 'Sign In');
		this.subtitle.set(
			(this.route.snapshot.data['subtitle'] as string) || 'Use your credentials to continue',
		);
		this.loginPath = (this.route.snapshot.data['loginPath'] as string) || 'api/admin/login';
		this.storageKey =
			(this.route.snapshot.data['authStorageKey'] as string) || MV_AUTH_TOKEN_STORAGE_KEY;
		this.redirectTo = (this.route.snapshot.data['redirectTo'] as string) || '/';

		if (this.authService.getToken(this.storageKey)) {
			this.router.navigateByUrl(this.redirectTo);
		}
	}

	protected togglePasswordVisibility(): void {
		this.showPassword.update((current) => !current);
	}

	protected onLogin(): void {
		if (!this.username.trim() || !this.password.trim()) {
			this.error.set('Username and password are required.');
			return;
		}

		this.error.set(null);
		this.isSubmitting.set(true);

		this.authService
			.login(this.loginPath, this.username.trim(), this.password)
			.pipe(finalize(() => this.isSubmitting.set(false)))
			.subscribe({
				next: (response) => {
					this.authService.saveToken(response.token, this.storageKey);
					this.password = '';
					const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
					this.router.navigateByUrl(returnUrl || this.redirectTo);
				},
				error: (error: { status?: number }) => {
					if (error.status === 401) {
						this.error.set('Invalid username or password.');
						return;
					}

					this.error.set('Unable to sign in right now.');
				},
			});
	}
}
